import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapStore } from '../store/useMapStore';
import { getFeatureShareKey } from '../utils/shareUtils';
import type {
  GisFeature,
  Geometry,
  Point,
  HealthFilters,
  HealthScope,
  TempleProperties,
  FuelFilters,
  FuelScope,
  FuelStation
} from '../types/gis';
import { normalizeDistrictName } from '../workers/gis/utils';
import { APP_VERSION } from '../constants';
import { trace } from 'firebase/performance';
import { performance } from '../lib/firebase';
import * as Sentry from "@sentry/react";
import { ENABLE_LAND_PARCEL } from '../config/features';

export const useGisWorker = () => {
  const workerRef = useRef<Worker | null>(null);
  const [isReady, setIsReady] = useState(false);
  const pendingSearchedArea = useRef<string | null>(null);
  const isExplicitLocationSource = useRef<boolean>(false);
  const { 
    activeLayer,
    activeDistrict,
    healthScope,
    healthFilters,
    searchResult,
    setPdsData, 
    setActiveDistrict, 
    setJurisdictionDetails, 
    setIsResolving, 
    setSearchSuggestions, 
    setSearchResult, 
    setSearchQuery, 
    setDistrictsData, 
    setStateBoundaryData,
    setNoDataFound,
    setActiveLayer,
    setAcData,
    setPcData,
    setConstituencyType,
    setPoliceBoundariesData,
    setPoliceStationsData,
    setSelectedPoliceStation,
    setPoliceResolution,
    setSelectedPostalOffices,
    setHealthManifest,
    setHealthPriorityData,
    setHealthDistrictData,
    setHealthSummary,
    setHealthScope,
    setSelectedHealthFacility,
    setIsHealthLoading,
    setSelectedLocalBodyV2,
    setIsV2Loading,
    setVeterinaryData,
    setSelectedVeterinaryFacility,
    setIsVeterinaryLoading,
    setRegistrationData,
    setSelectedRegistrationOffice,
    setIsSearching,
    setView,
    setSelectedLandParcel,
    setIsLandParcelLoading,
    setLandParcelError,
    fuelFilters,
    fuelRadiusKm,
    setFuelManifest,
    setFuelDistrictData,
    setFuelSummary,
    setFuelScope,
    setSelectedFuelStation,
    setIsFuelLoading
  } = useMapStore();

  const pincode = (searchResult?.properties?.PIN_CODE || searchResult?.properties?.pincode || searchResult?.properties?.pin_code)?.toString() || null;

  const loadDistricts = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_DISTRICTS' }), []);
  const loadStateBoundary = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_STATE_BOUNDARY' }), []);
  const loadPdsIndex = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_PDS_INDEX' }), []);
  const loadPincodes = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_PINCODES' }), []);
  const loadTnebStatewide = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_TNEB_STATEWIDE' }), []);
  const loadTnebDistrict = useCallback((district: string) => {
    workerRef.current?.postMessage({ type: 'LOAD_TNEB_DISTRICT', payload: { district } });
  }, []);
  const loadConstituencies = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_CONSTITUENCIES' }), []);
  const loadPoliceData = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_POLICE' }), []);
  const loadPostalDistrict = useCallback((district: string) => {
    workerRef.current?.postMessage({ type: 'LOAD_POSTAL_DISTRICT', payload: { district } });
  }, []);
  const loadHealthManifest = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_HEALTH_MANIFEST' }), []);
  const loadHealthPriority = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_HEALTH_PRIORITY' }), []);
  const loadHealthDistrict = useCallback((district: string, file_name: string) => {
    setIsHealthLoading(true);
    const t = performance ? trace(performance, 'load_health_district') : null;
    t?.start();
    workerRef.current?.postMessage({ type: 'LOAD_HEALTH_DISTRICT', payload: { district, file_name } });
    // Note: Since this is async worker communication, we might need a way to end the trace in the message handler
  }, [setIsHealthLoading]);

  const loadHealthSearchIndex = useCallback(() => 
    workerRef.current?.postMessage({ type: 'LOAD_HEALTH_SEARCH_INDEX' }), []);
    
  const filterHealth = useCallback((scope: HealthScope, filters: HealthFilters, district: string | null, pincode: string | null) => {
    workerRef.current?.postMessage({ type: 'FILTER_HEALTH', payload: { scope, filters, district, pincode } });
  }, []);
  
  const resolveHealthFacility = useCallback((id: string | number, nin: string | number | undefined, district: string | null) => {
    workerRef.current?.postMessage({ type: 'RESOLVE_HEALTH_FACILITY', payload: { id, nin, district } });
  }, []);

  const loadFuelManifest = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_FUEL_MANIFEST' }), []);
  const loadFuelIndex = useCallback(() => workerRef.current?.postMessage({ type: 'LOAD_FUEL_INDEX' }), []);
  const loadFuelDistrict = useCallback((district: string) => {
    setIsFuelLoading(true);
    workerRef.current?.postMessage({ type: 'LOAD_FUEL_DISTRICT', payload: { district } });
  }, [setIsFuelLoading]);
  const filterFuel = useCallback((scope: FuelScope, filters: FuelFilters, district: string | null, pincode: string | null, center?: { lat: number; lng: number } | null, radiusKm?: number) => {
    workerRef.current?.postMessage({ type: 'FILTER_FUEL', payload: { scope, filters, district, pincode, center, radiusKm } });
  }, []);
  const resolveFuelStation = useCallback((id: string, district: string | null) => {
    workerRef.current?.postMessage({ type: 'RESOLVE_FUEL_STATION', payload: { id, district } });
  }, []);

  const loadLocalBodiesV2 = useCallback(() => {
    workerRef.current?.postMessage({ type: 'LOAD_LOCAL_BODIES_V2' });
  }, []);

  const loadVeterinaryData = useCallback((district?: string | null, coords?: { lat: number; lng: number } | null) => {
    setIsVeterinaryLoading(true);
    workerRef.current?.postMessage({
      type: 'LOAD_VETERINARY',
      payload: { district: district || null, lat: coords?.lat ?? null, lng: coords?.lng ?? null }
    });
  }, [setIsVeterinaryLoading]);

  const loadRegistration = useCallback(() => {
    workerRef.current?.postMessage({ type: 'LOAD_REGISTRATION' });
  }, []);

  const loadRadioData = useCallback(() => {
    workerRef.current?.postMessage({ type: 'LOAD_RADIO' });
  }, []);

  const resolveLandParcel = useCallback(async (lat: number, lng: number) => {
    if (!ENABLE_LAND_PARCEL) return;
    setIsLandParcelLoading(true);
    setNoDataFound(false);
    setSelectedLandParcel(null);
    setLandParcelError(null);

    try {
      const response = await fetch(`/api/land-details?latitude=${lat}&longitude=${lng}`);
      if (!response.ok) {
        if (response.status === 504) {
          setLandParcelError('timeout');
        } else {
          setLandParcelError('error');
        }
        setIsLandParcelLoading(false);
        return;
      }
      
      const result = await response.json();
      const parcelData = result.data;
      
      if (!result.success || !parcelData || !parcelData.geojson_geom || parcelData.survey_number === 'N/A' || !parcelData.survey_number) {
        setNoDataFound(true, { lat, lng });
        setIsLandParcelLoading(false);
        return;
      }

      // The API returns geojson_geom which might be a string or object
      let geometry = parcelData.geojson_geom;
      if (typeof geometry === 'string') {
        try {
          geometry = JSON.parse(geometry);
        } catch (e) {
          console.error('Failed to parse geojson_geom string', e);
        }
      }

      const parcel: any = {
        type: 'Feature',
        properties: {
          district_name: parcelData.district_name,
          district_tamil_name: parcelData.district_tamil_name,
          taluk_name: parcelData.taluk_name,
          taluk_tamil_name: parcelData.taluk_tamil_name,
          village_name: parcelData.village_name || parcelData.revenue_town_name,
          village_tamil_name: parcelData.village_tamil_name || parcelData.revenue_town_tamil_name,
          survey_number: parcelData.survey_number,
          sub_division: parcelData.sub_division,
          ulpin: parcelData.ulpin,
          centroid: parcelData.centroid,
          area: parcelData.total_area || parcelData.area,
          rural_urban: parcelData.rural_urban,
          revenue_town_name: parcelData.revenue_town_name,
          revenue_town_tamil_name: parcelData.revenue_town_tamil_name,
          revenue_ward_name: parcelData.revenue_ward_name,
          revenue_ward_tamil_name: parcelData.revenue_ward_tamil_name,
          revenue_block_name: parcelData.revenue_block_name,
          revenue_block_tamil_name: parcelData.revenue_block_tamil_name
        },
        geometry: geometry
      };

      setSelectedLandParcel(parcel);
      setSearchQuery(`${parcelData.survey_number}/${parcelData.sub_division} - ${parcelData.village_name}`);
    } catch (err) {
      console.error('Error resolving land parcel:', err);
      setLandParcelError('error');
    } finally {
      setIsLandParcelLoading(false);
    }
  }, [setIsLandParcelLoading, setNoDataFound, setSelectedLandParcel, setSearchQuery, setLandParcelError]);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL('../workers/gis.worker.ts', import.meta.url),
      { type: 'module' }
    );

    workerRef.current.onmessage = (e) => {
      const { type, payload } = e.data;
      
      switch (type) {
        case 'READY':
          setIsReady(true);
          workerRef.current?.postMessage({ type: 'SET_VERSION', payload: { version: APP_VERSION } });
          break;
        case 'DISTRICTS_LOADED':
          setDistrictsData(payload);
          break;
        case 'STATE_BOUNDARY_LOADED':
          setStateBoundaryData(payload);
          break;
        case 'SUGGESTIONS_RESULT':
          setSearchSuggestions(payload);
          setIsSearching(false);
          break;
        case 'RESOLUTION_RESULT':
          if (payload && payload.found !== false) {
            const { keepSelection } = payload;
            setNoDataFound(false);
            if (payload.layer === 'TNEB') {
              setSearchResult(null, keepSelection);
              setJurisdictionDetails(payload.properties, payload.geometry);
              const sectionName = payload.properties.section_na || payload.properties.section_office || '';
              setSearchQuery(sectionName);
            } else if (payload.layer === 'POLICE') {
              setSearchResult(null, keepSelection);
              setPoliceResolution(payload);
              const stationName = payload.station?.properties.ps_name || payload.boundary?.properties.police_sta || '';
              setSearchQuery(stationName);
              setSearchQuery(stationName);
            } else if (payload.layer === 'VETERINARY') {
              setSearchResult(null, keepSelection);
              setSelectedVeterinaryFacility({
                type: 'Feature',
                properties: {
                  ...payload.properties,
                  is_explicit_source: isExplicitLocationSource.current
                },
                geometry: payload.geometry
              }, payload.pincodeGeometry || null);
              isExplicitLocationSource.current = false;
              setSearchQuery(payload.properties.village_na || '');
            } else if (payload.layer === 'REGISTRATION') {
              setSearchResult(null, keepSelection);
              setSelectedRegistrationOffice(
                {
                  type: 'Feature',
                  properties: payload.properties,
                  geometry: payload.office_location ? { type: 'Point', coordinates: payload.office_location } : payload.geometry
                },
                payload.geometry
              );
              setSearchQuery(payload.properties.sro_name || payload.properties.office_nam || '');
            } else if (payload.layer === 'LOCAL_BODIES_V2') {
              setSearchResult(null, keepSelection);
              const areaName = pendingSearchedArea.current;
              pendingSearchedArea.current = null; // Clear it
              
              const baseFeature = { 
                type: 'Feature' as const, 
                properties: { 
                  ...payload.properties, 
                  searched_area: areaName || undefined 
                }, 
                geometry: payload.geometry 
              };

              setSelectedLocalBodyV2(baseFeature);
              setSearchQuery(payload.properties.name);
              
              if (payload.properties.district) {
                setActiveDistrict(payload.properties.district);
              }

              // Lazy Progressive Enhancement: If no user area context was present (direct map click),
              // run a super-light async reverse geocode in the background and update the card silently!
              if (!areaName && payload.lat && payload.lng) {
                fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${payload.lat}&lon=${payload.lng}&zoom=18`, {
                  headers: { 'User-Agent': 'NammaMap-Progressive/1.0' }
                })
                .then(r => r.json())
                .then(data => {
                  if (data && data.address) {
                    const addr = data.address;
                    const resolved = addr.neighbourhood || addr.suburb || addr.village || addr.town || addr.residential || addr.city_district;
                    if (resolved) {
                       // Smoothly swap properties without full reload/rerender jitter
                       useMapStore.setState((state) => {
                         if (state.selectedLocalBodyV2) {
                           return {
                             selectedLocalBodyV2: {
                               ...state.selectedLocalBodyV2,
                               properties: { ...state.selectedLocalBodyV2.properties, searched_area: resolved }
                             }
                           };
                         }
                         return {};
                       });
                    }
                  }
                })
                .catch(err => console.warn('[Background Enhance] Address lookup skipped:', err));
              }
            } else if (payload.layer === 'TEMPLES') {
              setSearchResult({ type: 'Feature', properties: payload.properties, geometry: payload.geometry }, keepSelection, true);
              const temples = payload.properties.temples || [];
              const selectedTemple = payload.properties.selectedTemple || null;
              
              useMapStore.setState({
                selectedTemples: temples,
                selectedTemple: selectedTemple
              });
              
              if (selectedTemple) {
                setSearchQuery(selectedTemple.nm.en);
              } else if (payload.properties.pin_code || payload.properties.PIN_CODE || payload.properties.pincode) {
                const pc = (payload.properties.pin_code || payload.properties.PIN_CODE || payload.properties.pincode).toString();
                setSearchQuery(pc);
              }
            } else if (payload.layer === 'RADIO') {
              setSearchResult(null, keepSelection);
              useMapStore.setState({
                selectedRadioStation: {
                  type: 'Feature',
                  properties: payload.properties,
                  geometry: payload.geometry
                } as any
              });
              setSearchQuery(payload.properties.name);
            } else if (payload.layer === 'PINCODE' || payload.layer === 'PDS' || payload.layer === 'CONSTITUENCY' || payload.layer === 'HEALTH' || payload.layer === 'FUEL' || payload.layer === 'DISTRICT') {
              const currentState = useMapStore.getState();
              const isHealthDistrictRequest = payload.layer === 'HEALTH' && currentState.targetHealthScope === 'DISTRICT';
              const isFuelDistrictRequest = payload.layer === 'FUEL' && currentState.fuelScope === 'DISTRICT';
              
              if (isHealthDistrictRequest || isFuelDistrictRequest) {
                setSearchResult(null, keepSelection);
              } else {
                setSearchResult({ type: 'Feature', properties: payload.properties, geometry: payload.geometry }, keepSelection, true);
              }
              
              if (payload.layer === 'PINCODE' && payload.postalOffices) {
                setSelectedPostalOffices(payload.postalOffices);
              }

              const isHealthModule = useMapStore.getState().activeLayer === 'HEALTH';
              if (payload.layer === 'HEALTH' || (isHealthModule && (payload.layer === 'PINCODE' || payload.layer === 'DISTRICT'))) {
                const district = payload.properties.district || payload.properties.DISTRICT || payload.properties.DISTRICT_NAME || payload.properties.NAME || payload.properties.district_n;
                const pincode = payload.properties.PIN_CODE || payload.properties.pincode || payload.properties.pin_code;
                
                const currentState = useMapStore.getState();
                
                if (district) {
                  const districtName = district.toString();
                  console.log('[useGisWorker] Synchronizing Health context for district:', districtName);
                  setActiveDistrict(districtName);
                  
                  // Auto-switch scope based on what was found, but respect user preference if set
                  const targetScope = currentState.targetHealthScope;
                  const autoScope = (payload.layer === 'PINCODE' || pincode) ? 'PINCODE' : 'DISTRICT';
                  const newScope = targetScope || autoScope;
                  
                  setHealthScope(newScope);
                  // Reset target scope preference after it's been used
                  if (targetScope) {
                    currentState.setTargetHealthScope(null);
                  }

                  // Load district shard if manifest is available
                  const distManifest = currentState.healthManifest?.districts.find(d => 
                    d.district.toLowerCase().replace(/\s+/g, '') === districtName.toLowerCase().replace(/\s+/g, '')
                  );
                  
                  if (distManifest) {
                    setActiveDistrict(distManifest.district);
                    loadHealthDistrict(distManifest.district, distManifest.file_name);
                  } else {
                    console.warn('[useGisWorker] District manifest not found for:', districtName);
                    setIsHealthLoading(false);
                  }
                } else {
                  if (isHealthModule) setIsHealthLoading(false);
                }
              }

              const isFuelModule = useMapStore.getState().activeLayer === 'FUEL';
              if (payload.layer === 'FUEL' || (isFuelModule && (payload.layer === 'PINCODE' || payload.layer === 'DISTRICT'))) {
                const district = payload.properties.district || payload.properties.DISTRICT || payload.properties.DISTRICT_NAME || payload.properties.NAME || payload.properties.district_n;
                const pincode = payload.properties.PIN_CODE || payload.properties.pincode || payload.properties.pin_code;
                
                const currentState = useMapStore.getState();

                if (district) {
                  const districtName = district.toString();
                  const districtKey = normalizeDistrictName(districtName);
                  const distManifest = currentState.fuelManifest?.districts.find(
                    d => normalizeDistrictName(d.district) === districtKey
                  );

                  setActiveDistrict(distManifest?.district ?? districtName);
                  if (currentState.fuelScope !== 'STATE') {
                    setFuelScope(currentState.fuelScope === 'DISTRICT' ? 'DISTRICT' : ((payload.layer === 'PINCODE' || pincode) ? 'PINCODE' : 'DISTRICT'));
                  }

                  if (distManifest) {
                    loadFuelDistrict(distManifest.district);
                  } else {
                    // District has no fuel data; the reactive effect will fall back to statewide.
                    setIsFuelLoading(false);
                  }
                } else {
                  if (isFuelModule) setIsFuelLoading(false);
                }
              }

              if (payload.layer === 'PDS') {
                const district = payload.properties.district || payload.properties.DISTRICT || payload.properties.DISTRICT_NAME || payload.properties.NAME;
                const pincode = (payload.properties.pin_code || payload.properties.PIN_CODE || payload.properties.pincode)?.toString();
                workerRef.current?.postMessage({ 
                  type: 'LOAD_PDS', 
                  payload: { district, pincode, boundary: payload.geometry } 
                });
              }
            }
          } else {
            const { lat, lng, isInsideState } = payload || {};
            if (isInsideState) {
              setNoDataFound(true, lat && lng ? { lat, lng } : null);
            } else {
              setNoDataFound(false);
            }
          }
          setIsResolving(false);
          setIsV2Loading(false);
          // End resolution trace if it was started
          break;
        case 'FUEL_MANIFEST_LOADED':
          setFuelManifest(payload);
          break;
        case 'FUEL_INDEX_LOADED':
          // Statewide rows are cached in the worker; nothing to mirror on the main thread.
          break;
        case 'FUEL_DISTRICT_LOADED': {
          setActiveDistrict(payload.district);
          const s = useMapStore.getState();
          const pc = (s.searchResult?.properties?.PIN_CODE || s.searchResult?.properties?.pincode || s.searchResult?.properties?.pin_code)?.toString() || null;
          filterFuel(s.fuelScope, s.fuelFilters, payload.district, pc);
          break;
        }
        case 'FUEL_FILTERED':
          setFuelDistrictData({ type: 'FeatureCollection', features: payload.features });
          setFuelSummary(payload.summary);
          setIsFuelLoading(false);
          break;
        case 'FUEL_STATION_RESOLVED':
          setSelectedFuelStation((payload as FuelStation) || null);
          setIsFuelLoading(false);
          break;
        case 'TNEB_STATEWIDE_LOADED':
          // statewide search index ready
          break;
        case 'TNEB_DISTRICT_LOADED':
          setActiveDistrict(payload.district);
          break;
        case 'PDS_LOADED':
          setPdsData(payload.data);
          setActiveDistrict(payload.district);
          break;
        case 'CONSTITUENCIES_LOADED':
          setAcData(payload.ac);
          setPcData(payload.pc);
          break;

        case 'LOCAL_BODIES_V2_LOADED':
          setIsV2Loading(false);
          break;
        case 'VETERINARY_LOADED':
          setIsVeterinaryLoading(false);
          setVeterinaryData(payload);
          break;
        case 'REGISTRATION_LOADED':
          // No specific state update needed yet beyond ready
          break;
        case 'RADIO_LOADED':
          useMapStore.getState().setRadioData(payload);
          break;
        case 'POLICE_LOADED':
          setPoliceBoundariesData(payload.boundaries);
          setPoliceStationsData(payload.stations);
          break;
        case 'AUTO_TRIGGER_PDS':
          workerRef.current?.postMessage({ type: 'LOAD_PDS', payload });
          break;
        case 'POSTAL_OFFICES_LOADED':
          // Optional: handle if UI needs to know
          break;
        case 'HEALTH_MANIFEST_LOADED':
          console.log('[Worker] Health Manifest Loaded', payload);
          setHealthManifest(payload);
          break;
        case 'HEALTH_PRIORITY_LOADED':
          console.log('[Worker] Health Priority Loaded', payload);
          setHealthPriorityData(payload);
          break;
        case 'HEALTH_DISTRICT_LOADED': {
          console.log('[Worker] Health District Loaded', payload.district);
          setActiveDistrict(payload.district);
          // Trigger filter now that data is ready
          const s = useMapStore.getState();
          const pc = (s.searchResult?.properties?.PIN_CODE || s.searchResult?.properties?.pincode || s.searchResult?.properties?.pin_code)?.toString() || null;
          filterHealth(s.healthScope, s.healthFilters, payload.district, pc);
          break;
        }
        case 'HEALTH_SEARCH_INDEX_LOADED':
          console.log('[Worker] Health Search Index Loaded');
          break;
        case 'HEALTH_FILTERED':
          console.log('[Worker] Health Filtered', payload.summary);
          if (payload.scope === 'STATE') {
            setHealthPriorityData({ type: 'FeatureCollection', features: payload.features });
          } else {
            setHealthDistrictData({ type: 'FeatureCollection', features: payload.features });
          }
          setHealthSummary(payload.summary);
          setHealthScope(payload.scope);
          setIsHealthLoading(false);
          break;
        case 'HEALTH_FACILITY_RESOLVED':
          if (payload) {
            const cur = useMapStore.getState().selectedHealthFacility;
            if (!cur || cur.id === payload.id || cur.properties?.nin_number === payload.properties?.nin_number) {
              setSelectedHealthFacility(payload);
            }
          }
          break;
        case 'ERROR':
          console.error('[Worker Error]', payload);
          if (payload === 'NETWORK_FAILURE') {
            useMapStore.getState().setHasNetworkError(true);
          } else {
            Sentry.captureException(new Error(`GIS Worker Error: ${payload}`));
          }
          setIsResolving(false);
          setIsHealthLoading(false);
          setIsFuelLoading(false);
          setIsV2Loading(false);
          setIsSearching(false);
          break;
      }
    };

    workerRef.current.postMessage({ type: 'INIT_DB' });

    return () => {
      workerRef.current?.terminate();
    };
  }, [
    setActiveDistrict, 
    setDistrictsData, 
    setIsResolving, 
    setJurisdictionDetails, 
    setPdsData, 
    setSearchQuery, 
    setSearchResult, 
    setSearchSuggestions, 
    setStateBoundaryData,
    setNoDataFound,
    loadPdsIndex,
    setAcData,
    setPcData,
    setPoliceBoundariesData,
    setPoliceStationsData,
    setSelectedPoliceStation,
    setPoliceResolution,
    setSelectedPostalOffices,
    setHealthManifest,
    setHealthPriorityData,
    setHealthDistrictData,
    setHealthSummary,
    setHealthScope,
    setIsV2Loading,
    setIsHealthLoading,
    setIsVeterinaryLoading,
    setVeterinaryData,
    setRegistrationData,
    setSelectedRegistrationOffice,
    filterHealth,
    setIsSearching,
    setFuelManifest,
    setFuelDistrictData,
    setFuelSummary,
    setFuelScope,
    setSelectedFuelStation,
    setIsFuelLoading,
    loadFuelDistrict,
    filterFuel
  ]);


  // Reactive filtering for Health Module with Debounce
  const filterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (activeLayer === 'HEALTH' && isReady) {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
      
      filterTimeoutRef.current = setTimeout(() => {
        const state = useMapStore.getState();
        const currentPincode = (state.searchResult?.properties?.PIN_CODE || state.searchResult?.properties?.pincode || state.searchResult?.properties?.pin_code)?.toString() || null;
        
        console.log('[useGisWorker] Triggering filterHealth with latest state:', { 
          scope: state.healthScope, 
          district: state.activeDistrict, 
          pincode: currentPincode 
        });
        
        filterHealth(state.healthScope, state.healthFilters, state.activeDistrict, currentPincode);
      }, 150); // 150ms debounce for stability
    }
    return () => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    };
  }, [activeLayer, isReady, healthScope, healthFilters, activeDistrict, pincode, filterHealth]);

  // Reactive filtering for Fuel Module with Debounce
  const fuelFilterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFuelPincodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (activeLayer !== 'FUEL' || !isReady) return;

    const state = useMapStore.getState();
    if (!state.fuelManifest) loadFuelManifest();
    // The packed index backs both search suggestions and statewide rendering.
    loadFuelIndex();

    if (fuelFilterTimeoutRef.current) clearTimeout(fuelFilterTimeoutRef.current);

    fuelFilterTimeoutRef.current = setTimeout(() => {
      setIsFuelLoading(true);
      const current = useMapStore.getState();
      const currentPincode = (
        current.searchResult?.properties?.PIN_CODE ||
        current.searchResult?.properties?.pincode ||
        current.searchResult?.properties?.pin_code ||
        current.selectedFuelStation?.properties?.pincode
      )?.toString() || null;

      if (currentPincode) {
        lastFuelPincodeRef.current = currentPincode;
      }

      const effectivePincode = currentPincode || lastFuelPincodeRef.current;

      const currentDistrict = current.activeDistrict || (
        current.searchResult?.properties?.district ||
        current.searchResult?.properties?.district_name ||
        current.searchResult?.properties?.DISTRICT ||
        current.searchResult?.properties?.district_n
      )?.toString() || null;

      // Center point calculation for 5km radial buffer in My Area / PINCODE scope
      let center: { lat: number; lng: number } | null = null;
      if (current.searchResult?.geometry) {
        const geom = current.searchResult.geometry;
        if (geom.type === 'Point') {
          const coords = (geom as Point).coordinates;
          if (Array.isArray(coords) && coords.length >= 2 && Number.isFinite(coords[1]) && Number.isFinite(coords[0])) {
            center = { lat: coords[1], lng: coords[0] };
          }
        } else if (geom.type === 'Polygon') {
          const ring = (geom.coordinates as [number, number][][])[0];
          if (Array.isArray(ring) && ring.length > 0) {
            let sumLat = 0, sumLng = 0, count = 0;
            for (const pt of ring) {
              if (Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[1]) && Number.isFinite(pt[0])) {
                sumLng += pt[0];
                sumLat += pt[1];
                count++;
              }
            }
            if (count > 0) center = { lat: sumLat / count, lng: sumLng / count };
          }
        } else if (geom.type === 'MultiPolygon') {
          const polys = geom.coordinates as [number, number][][][];
          if (Array.isArray(polys) && polys.length > 0 && polys[0].length > 0) {
            const ring = polys[0][0];
            let sumLat = 0, sumLng = 0, count = 0;
            for (const pt of ring) {
              if (Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[1]) && Number.isFinite(pt[0])) {
                sumLng += pt[0];
                sumLat += pt[1];
                count++;
              }
            }
            if (count > 0) center = { lat: sumLat / count, lng: sumLng / count };
          }
        }
      }

      if (!center && current.userLocation && Number.isFinite(current.userLocation.lat) && Number.isFinite(current.userLocation.lng)) {
        center = { lat: current.userLocation.lat, lng: current.userLocation.lng };
      }

      if (!center && current.selectedFuelStation?.geometry?.type === 'Point') {
        const coords = (current.selectedFuelStation.geometry as Point).coordinates;
        if (Array.isArray(coords) && coords.length >= 2 && Number.isFinite(coords[1]) && Number.isFinite(coords[0])) {
          center = { lat: coords[1], lng: coords[0] };
        }
      }



      const pincodeForWorker = effectivePincode || null;
      const radiusKm = current.fuelRadiusKm || 5.0;
      filterFuel('PINCODE', current.fuelFilters, currentDistrict, pincodeForWorker, center, radiusKm);
    }, 150);

    return () => {
      if (fuelFilterTimeoutRef.current) clearTimeout(fuelFilterTimeoutRef.current);
    };
  }, [activeLayer, isReady, fuelFilters, fuelRadiusKm, activeDistrict, pincode, filterFuel, loadFuelManifest, loadFuelIndex, setIsFuelLoading]);

  const resolveLocation = useCallback((lat: number, lng: number, layer: string, keepSelection: boolean = false, pincode?: string, stationCode?: string, veterinaryKey?: string, isExplicitSource: boolean = false, registrationKey?: string, templeKey?: string) => {
    setIsResolving(true);
    isExplicitLocationSource.current = isExplicitSource;
    // Reset pending area unless this call sets it explicitly (e.g. clicking map clears pending search)
    pendingSearchedArea.current = null;
    if (layer === 'LOCAL_BODIES_V2') setIsV2Loading(true);
    setSearchResult(null, keepSelection);
    const t = performance ? trace(performance, 'resolve_location') : null;
    t?.start();
    t?.putAttribute('layer', layer);
    workerRef.current?.postMessage({
      type: 'RESOLVE_LOCATION',
      payload: { lat, lng, layer, keepSelection, pincode, stationCode, veterinaryKey, registrationKey, templeKey, constituencyType: useMapStore.getState().constituencyType }
    });
  }, [setSearchResult, setIsResolving, setIsV2Loading]);

  const getSuggestions = useCallback((query: string, layer: string) => {
    if (query.trim().length > 0) {
      setIsSearching(true);
    }
    workerRef.current?.postMessage({ type: 'GET_SUGGESTIONS', payload: { query, layer } });
  }, [setIsSearching]);

  const selectSuggestion = useCallback((item: GisFeature, currentLayer: string) => {
    if (currentLayer === 'LOCAL_BODIES_V2') {
      setIsV2Loading(true);
      if (item.suggestionType === 'GLOBAL_PLACE') {
        pendingSearchedArea.current = (item.properties.main_text || item.properties.name)?.toString() || null;
      }
      workerRef.current?.postMessage({ type: 'SELECT_SUGGESTION', payload: { suggestion: item, layer: currentLayer } });
      return;
    }

    if (item.suggestionType === 'COORDINATES' || item.suggestionType === 'GLOBAL_PLACE') {
      const { lat, lng, viewport, bounds } = item.properties as any;
      if (lat !== undefined && lng !== undefined) {
        setSearchResult(item, false, true);
        
        if (item.suggestionType === 'GLOBAL_PLACE') {
          pendingSearchedArea.current = (item.properties.main_text || item.properties.name)?.toString() || null;
        }
        
        if (currentLayer === 'LAND_PARCEL') {
          resolveLandParcel(lat, lng);
        } else {
          resolveLocation(lat, lng, currentLayer, false, undefined, undefined, undefined, true);
        }
        
        // Use viewport or bounds for better framing if available
        if (viewport || bounds) {
          // This will be handled by MapController if we pass the feature to setSearchResult
        } else {
          setView([lat, lng], 15);
        }
      }
      return;
    }

    if (item.suggestionType === 'RADIO_STATION') {
      if (currentLayer !== 'RADIO') setActiveLayer('RADIO');
      const [lng, lat] = item.geometry.type === 'Point' 
        ? (item.geometry.coordinates as [number, number])
        : [0, 0];
      resolveLocation(lat, lng, 'RADIO');
    } else if (item.suggestionType === 'TNEB_SECTION') {
      if (currentLayer !== 'TNEB') setActiveLayer('TNEB');
      const [lng, lat] = item.geometry.type === 'Point' 
        ? (item.geometry.coordinates as [number, number])
        : (item.properties.office_location as [number, number] || [78.6569, 11.1271]); // Fallback
      resolveLocation(lat, lng, 'TNEB');
    } else if (item.suggestionType === 'PDS_SHOP') {
      if (currentLayer !== 'PDS') setActiveLayer('PDS');
      const [lng, lat] = item.geometry.type === 'Point' 
        ? (item.geometry.coordinates as [number, number])
        : (item.properties.office_location as [number, number] || [78.6569, 11.1271]); // Fallback
      const district = item.properties.district as string;
      if (district) {
        workerRef.current?.postMessage({ type: 'LOAD_PDS', payload: { district, boundary: item.geometry } });
      }
      // Trigger resolution for the shop itself to show the card
      setSearchResult(item, false, true);
      resolveLocation(lat, lng, 'PDS', true);
    } else if (item.suggestionType === 'DISTRICT') {
      // Switch to PINCODE if we want to show the district boundary in that layer context
      // or PDS if that was the intent. For now, stay in current layer but focus district.
      setSearchResult(item, false, true);
      const district = (item.properties.district || item.properties.DISTRICT || item.properties.NAME || item.properties.district_n || item.properties.DISTRICT_NAME || '').toString();
      const targetLayer = currentLayer;
      if (district && targetLayer === 'PDS') {
        workerRef.current?.postMessage({ type: 'LOAD_PDS', payload: { district, boundary: item.geometry } });
      }
    } else if (item.suggestionType === 'CONSTITUENCY') {
      if (currentLayer !== 'CONSTITUENCY') setActiveLayer('CONSTITUENCY');
      // Detect if it's AC or PC based on properties
      const isPc = !!item.properties.parliame_1 && !item.properties.assembly_c;
      setConstituencyType(isPc ? 'PC' : 'AC');
      setSearchResult(item, false, true);
    } else if (item.suggestionType === 'POLICE_STATION') {
      if (currentLayer !== 'POLICE') setActiveLayer('POLICE');
      const [lng, lat] = item.geometry.type === 'Point' 
        ? (item.geometry.coordinates as [number, number])
        : (item.properties.station_location as [number, number] || [78.6569, 11.1271]); // Fallback
      resolveLocation(lat, lng, 'POLICE', false, undefined, item.properties.ps_code as string);
    } else if (item.suggestionType === 'VETERINARY_FACILITY') {
      if (currentLayer !== 'VETERINARY') setActiveLayer('VETERINARY');
      setSearchResult(item, false, true);
      const [lng, lat] = item.geometry.type === 'Point' 
        ? (item.geometry.coordinates as [number, number])
        : [78.6569, 11.1271];
      const vetKey = getFeatureShareKey(item as any, 'VETERINARY') || undefined;
      resolveLocation(lat, lng, 'VETERINARY', false, undefined, undefined, vetKey, true);
      setView([lat, lng], 16);
    } else if (item.suggestionType === 'TEMPLE_FACILITY') {
      if (currentLayer !== 'TEMPLES') setActiveLayer('TEMPLES');
      setSearchResult(item, false, true);
      const templeObj = item.properties.temple as unknown as TempleProperties;
      if (templeObj) {
        const tLat = parseFloat(templeObj.g.lat.toString());
        const tLng = parseFloat(templeObj.g.lng.toString());
        const templeKey = `temple_${templeObj.g.dc}_${templeObj.id}`;
        
        useMapStore.setState({
          selectedTemple: templeObj
        });
        
        resolveLocation(tLat, tLng, 'TEMPLES', false, undefined, undefined, undefined, true, undefined, templeKey);
        setView([tLat, tLng], 16);
      }
    } else if ((item.suggestionType as any) === 'RADIO_STATION') {
      if (currentLayer !== 'RADIO') setActiveLayer('RADIO');
      setSearchResult(item, false, true);
      useMapStore.getState().setSelectedRadioStation(item as any);
      const [lng, lat] = item.geometry.type === 'Point' ? (item.geometry.coordinates as [number, number]) : [78.6569, 11.1271];
      resolveLocation(lat, lng, 'RADIO', false, undefined, undefined, undefined, true);
      setView([lat, lng], 16);
    } else if (item.suggestionType === 'HEALTH_FACILITY') {
      if (currentLayer !== 'HEALTH') setActiveLayer('HEALTH');
      setSearchResult(item, false, true);
      setSelectedHealthFacility(item as any);
      setHealthScope('DISTRICT');
      
      const district = (item.properties.district || item.properties.district_n)?.toString();
      if (district) setActiveDistrict(district);

      // Find shard from manifest
      const distManifest = useMapStore.getState().healthManifest?.districts.find(d => 
        d.district.toLowerCase().replace(/\s+/g, '') === district?.toLowerCase().replace(/\s+/g, '')
      );
      if (distManifest && district) {
        setActiveDistrict(distManifest.district);
        loadHealthDistrict(distManifest.district, distManifest.file_name);
      }
    } else if (item.suggestionType === 'FUEL_STATION') {
      if (currentLayer !== 'FUEL') setActiveLayer('FUEL');
      setSearchResult(item, false, true);
      setFuelScope('DISTRICT');
      setIsFuelLoading(true);

      const district = item.properties.district?.toString() || null;
      const districtKey = district ? normalizeDistrictName(district) : '';
      const distManifest = useMapStore.getState().fuelManifest?.districts.find(
        d => normalizeDistrictName(d.district) === districtKey
      );
      const resolvedDistrict = distManifest?.district ?? district;
      if (resolvedDistrict) setActiveDistrict(resolvedDistrict);

      // The suggestion row is thin; pull the full record so the card can show
      // address, contact and amenities.
      resolveFuelStation(item.properties.id?.toString() || '', resolvedDistrict);
      if (distManifest) loadFuelDistrict(distManifest.district);
    } else {
      // PINCODE or Area
      const pin = (item.properties.pin_code || item.properties.PIN_CODE || item.properties.pincode)?.toString();
      
      if (pin && (currentLayer === 'PINCODE' || currentLayer === 'CONSTITUENCY' || currentLayer === 'HEALTH' || currentLayer === 'TEMPLES' || currentLayer === 'PDS' || currentLayer === 'FUEL')) {
        resolveLocation(0, 0, currentLayer, false, pin);
      } else {
        setSearchResult(item, false, true);
      }

      const district = item.properties.district || item.properties.DISTRICT || item.properties.DISTRICT_NAME || item.properties.NAME;
      if (district && currentLayer === 'PDS') {
        workerRef.current?.postMessage({ type: 'LOAD_PDS', payload: { district, pincode: pin, boundary: item.geometry } });
      }
    }
  }, [resolveLocation, setSearchResult, setActiveLayer, setConstituencyType, loadHealthDistrict, setSelectedHealthFacility, setHealthScope, setActiveDistrict]);

  const loadPds = useCallback((district: string, boundary: Geometry, pincode?: string) => {
    workerRef.current?.postMessage({ type: 'LOAD_PDS', payload: { district, boundary, pincode } });
  }, []);

  return { 
    isReady, 
    loadDistricts, 
    loadStateBoundary, 
    loadPdsIndex, 
    loadPincodes, 
    loadTnebStatewide,
    loadTnebDistrict, 
    loadPds, 
    loadConstituencies, 
    loadPoliceData, 
    loadPostalDistrict, 
    loadHealthManifest, 
    loadHealthPriority, 
    loadHealthDistrict, 
    loadHealthSearchIndex,
    loadLocalBodiesV2,
    loadVeterinaryData,
    loadRegistration,
    loadRadioData,
    filterHealth,
    resolveHealthFacility,
    loadFuelManifest,
    loadFuelDistrict,
    loadFuelIndex,
    filterFuel,
    resolveFuelStation,
    resolveLocation, 
    resolveLandParcel,
    getSuggestions, 
    selectSuggestion 
  };
};
